import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import {
  Plus, FileText, Shield,
} from 'lucide-react';

import HierarchyBreadcrumb from '../components/HierarchyBreadcrumb';
import ProjectTypeBadge from '../components/ProjectTypeBadge';
import MilestoneCard from '../components/MilestoneCard';
import BurnDownChart from '../components/BurnDownChart';
import BudgetVsActualGauge from '../components/BudgetVsActualGauge';
import MilestoneProgressWidget from '../components/MilestoneProgressWidget';
import DeadlineBanner from '../components/DeadlineBanner';
import DeadlineBadge from '../components/DeadlineBadge';
import {
  getStatusLabel, getPriorityLabel, getProjectStatusLabel,
} from '../types';
import type { Ticket } from '../types';

// ============================================================
// CreateMilestoneModal
// ============================================================
function CreateMilestoneModal({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [estimatedHours, setEstimatedHours] = useState('');

  const mutation = useMutation({
    mutationFn: () => api.createMilestone(projectId, {
      name,
      description: description || undefined,
      due_date: dueDate || undefined,
      estimated_hours: estimatedHours ? parseFloat(estimatedHours) : undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      onClose();
    },
  });

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-content max-w-md w-full p-6">
        <h2 className="text-xl font-bold text-surface-900 mb-4">{t('project.newMilestone')}</h2>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
          <div>
            <label className="label">{t('project.milestoneName')}</label>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
          </div>
          <div>
            <label className="label">{t('project.milestoneDesc')}</label>
            <textarea className="input min-h-[60px]" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('project.milestoneDueDate')}</label>
              <input type="date" className="input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
            <div>
              <label className="label">{t('project.milestoneEstHours')}</label>
              <input type="number" step="0.5" className="input" value={estimatedHours} onChange={(e) => setEstimatedHours(e.target.value)} />
            </div>
          </div>
          {mutation.error && <p className="text-sm text-danger-600">{t('project.milestoneCreateError')}</p>}
          <div className="flex gap-3">
            <button type="submit" disabled={mutation.isPending} className="btn btn-primary flex-1">
              {mutation.isPending ? t('common.creating') : t('project.createMilestone')}
            </button>
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">{t('common.cancel')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// TicketList (shared)
// ============================================================
function TicketList({ tickets }: { tickets: Ticket[] }) {
  const { t } = useTranslation();
  if (tickets.length === 0) {
    return <div className="text-center py-6 text-surface-400 text-sm">{t('project.noTickets')}</div>;
  }

  return (
    <div className="divide-y divide-surface-100">
      {tickets.map((ticket) => (
        <Link key={ticket.id} to={`/tickets/${ticket.id}`}
          className="flex items-center justify-between p-3 hover:bg-surface-50 transition-colors">
          <div className="flex items-center gap-3 min-w-0">
            <FileText size={16} className="text-surface-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-surface-800 truncate">{ticket.title}</p>
              <p className="text-xs text-surface-400">
                {ticket.agent_name || t('common.unassigned')}
                {ticket.estimated_hours ? ` · ${ticket.actual_hours}/${ticket.estimated_hours} ${t('common.hoursShort')}` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {ticket.due_date && <DeadlineBadge dueDate={ticket.due_date} />}
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              ticket.priority === 'urgent' ? 'bg-danger-100 text-danger-700' :
              ticket.priority === 'high' ? 'bg-orange-100 text-orange-700' :
              'bg-surface-100 text-surface-600'
            }`}>
              {getPriorityLabel(ticket.priority, t)}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              ticket.status === 'open' ? 'bg-info-100 text-info-700' :
              ticket.status === 'in_progress' ? 'bg-warning-100 text-warning-700' :
              ticket.status === 'waiting' ? 'bg-orange-100 text-orange-700' :
              'bg-success-100 text-success-700'
            }`}>
              {getStatusLabel(ticket.status, t)}
            </span>
          </div>
        </Link>
      ))}
    </div>
  );
}

// ============================================================
// HourBankWidget
// ============================================================
function HourBankWidget({ used, total }: { used: number; total: number }) {
  const { t } = useTranslation();
  const remaining = Math.max(0, total - used);
  const pct = total > 0 ? (used / total) * 100 : 0;
  const alertLevel = pct >= 90 ? 'red' : pct >= 80 ? 'amber' : 'green';

  const radius = 80;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (Math.min(pct, 100) / 100) * circumference;
  const strokeColor = alertLevel === 'red' ? '#ef4444' : alertLevel === 'amber' ? '#f59e0b' : '#22c55e';

  return (
    <div className="card">
      <h3 className="text-sm font-semibold text-surface-700 mb-4">{t('project.hourBankStatus')}</h3>

      {pct >= 80 && (
        <div className={`px-3 py-2 rounded-lg text-xs font-medium mb-3 ${
          pct >= 90 ? 'bg-danger-50 text-danger-700 border border-danger-100' : 'bg-warning-50 text-warning-700 border border-warning-200'
        }`}>
          {pct >= 90 ? `⚠️ ${t('project.lessThan10')}` : `⚠️ ${t('project.lessThan20')}`}
        </div>
      )}

      <div className="flex items-center justify-center">
        <svg width={200} height={200} viewBox="0 0 200 200">
          <circle cx={100} cy={100} r={radius} fill="none" stroke="#e2e8f0" strokeWidth={16} />
          <circle cx={100} cy={100} r={radius} fill="none" stroke={strokeColor} strokeWidth={16}
            strokeDasharray={circumference} strokeDashoffset={dashOffset}
            strokeLinecap="round" transform="rotate(-90 100 100)" className="transition-all duration-700" />
          <text x={100} y={88} textAnchor="middle" className="text-3xl font-bold" fill="#1f2937">
            {remaining.toFixed(1)}
          </text>
          <text x={100} y={110} textAnchor="middle" className="text-sm" fill="#6b7280">
            {t('project.hoursRemaining')}
          </text>
          <text x={100} y={130} textAnchor="middle" className="text-xs" fill="#9ca3af">
            {t('project.outOf')}{total}
          </text>
        </svg>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-4 text-center">
        <div className="p-2 bg-surface-50 rounded-lg">
          <p className="text-lg font-bold text-surface-800">{total}</p>
          <p className="text-xs text-surface-500">{t('project.totalLabel')}</p>
        </div>
        <div className="p-2 bg-info-50 rounded-lg">
          <p className="text-lg font-bold text-info-700">{used.toFixed(1)}</p>
          <p className="text-xs text-surface-500">{t('project.usedLabel')}</p>
        </div>
        <div className={`p-2 rounded-lg ${alertLevel === 'red' ? 'bg-danger-50' : alertLevel === 'amber' ? 'bg-warning-50' : 'bg-success-50'}`}>
          <p className={`text-lg font-bold ${alertLevel === 'red' ? 'text-danger-700' : alertLevel === 'amber' ? 'text-warning-700' : 'text-success-700'}`}>
            {remaining.toFixed(1)}
          </p>
          <p className="text-xs text-surface-500">{t('project.remainingLabel')}</p>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Main ProjectPage
// ============================================================
export default function ProjectPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const user = useAuthStore((s) => s.user);
  const [showCreateMilestone, setShowCreateMilestone] = useState(false);

  const { data: projectData, isLoading } = useQuery({
    queryKey: ['project', id],
    queryFn: () => api.getProject(id!),
    enabled: !!id,
  });

  const { data: ticketsData } = useQuery({
    queryKey: ['project-tickets', id],
    queryFn: () => api.getProjectTickets(id!, { limit: 100 }),
    enabled: !!id,
  });

  const project = projectData?.data;
  const tickets = ticketsData?.data ?? [];
  const isAdmin = user?.role === 'admin';

  if (isLoading) return <div className="flex justify-center py-12"><div className="spinner" /></div>;
  if (!project) return <div className="card text-center py-12 text-surface-500">{t('project.notFound')}</div>;

  const stats = project.stats;
  const milestones = project.milestones || [];

  const breadcrumbItems = [
    { label: project.organization_name || t('project.orgLabel'), href: `/organizations/${project.organization_id}` },
    { label: project.name },
  ];

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <HierarchyBreadcrumb items={breadcrumbItems} />

      {/* Deadline banner */}
      <DeadlineBanner
        dueDate={project.end_date}
        source="project"
        warningDays={project.deadline_warning_days || 7}
        entityLabel={t('project.theProject')}
      />

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="page-title">{project.name}</h1>
            <ProjectTypeBadge type={project.type} size="md" />
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              project.status === 'active' ? 'bg-success-100 text-success-700' :
              project.status === 'on_hold' ? 'bg-warning-100 text-warning-700' :
              project.status === 'completed' ? 'bg-info-100 text-info-700' :
              'bg-surface-100 text-surface-600'
            }`}>
              {getProjectStatusLabel(project.status, t)}
            </span>
          </div>
          {project.description && <p className="text-sm text-surface-500 mt-1">{project.description}</p>}
        </div>
        {/* Settings link removed — inline editing is available below */}
      </div>

      {/* Stats bar */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="card text-center py-3">
            <p className="text-2xl font-bold text-surface-800">{stats.tickets_total}</p>
            <p className="text-xs text-surface-500">{t('project.totalTickets')}</p>
          </div>
          <div className="card text-center py-3">
            <p className="text-2xl font-bold text-success-600">{stats.tickets_closed}</p>
            <p className="text-xs text-surface-500">{t('project.closed')}</p>
          </div>
          <div className="card text-center py-3">
            <p className="text-2xl font-bold text-info-600">{stats.total_actual_hours.toFixed(1)}</p>
            <p className="text-xs text-surface-500">{t('project.actualHours')}</p>
          </div>
          <div className="card text-center py-3">
            <p className="text-2xl font-bold text-primary-600">{stats.completion_percentage}%</p>
            <p className="text-xs text-surface-500">{t('project.completed')}</p>
          </div>
        </div>
      )}

      {/* SLA override — admin only */}
      {isAdmin && (
        <ProjectSlaOverride projectId={id!} project={project} />
      )}

      {/* Type-specific layout */}
      {project.type === 'service' && (
        <ServiceLayout tickets={tickets} />
      )}

      {project.type === 'hour_bank' && (
        <HourBankLayout
          tickets={tickets}
          totalBudgetHours={project.total_budget_hours || 0}
          actualHours={stats?.total_actual_hours || 0}
          projectId={id!}
        />
      )}

      {project.type === 'development' && (
        <DevelopmentLayout
          milestones={milestones}
          isAdmin={isAdmin}
          onCreateMilestone={() => setShowCreateMilestone(true)}
        />
      )}

      {showCreateMilestone && (
        <CreateMilestoneModal projectId={id!} onClose={() => setShowCreateMilestone(false)} />
      )}
    </div>
  );
}

// ============================================================
// Service Layout
// ============================================================
function ServiceLayout({ tickets }: { tickets: Ticket[] }) {
  const { t } = useTranslation();
  const openTickets = tickets.filter((tk) => tk.status !== 'closed');

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-bold text-surface-800">{t('project.openTickets')} ({openTickets.length})</h2>
      </div>
      <TicketList tickets={openTickets} />
    </div>
  );
}

// ============================================================
// Hour Bank Layout
// ============================================================
function HourBankLayout({ tickets, totalBudgetHours, actualHours, projectId }: {
  tickets: Ticket[];
  totalBudgetHours: number;
  actualHours: number;
  projectId: string;
}) {
  const { t } = useTranslation();

  const { data: budgetData } = useQuery({
    queryKey: ['project-budget-actual', projectId],
    queryFn: () => api.getProjectBudgetActual(projectId),
    enabled: !!projectId,
  });

  const { data: burndownData } = useQuery({
    queryKey: ['project-burndown', projectId],
    queryFn: () => api.getProjectBurndown(projectId),
    enabled: !!projectId,
  });

  const budget = budgetData?.data;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 space-y-4">
        <HourBankWidget used={actualHours} total={totalBudgetHours} />
        {budget && budget.budget_amount > 0 && (
          <BudgetVsActualGauge fixedPrice={budget.budget_amount} actualCost={budget.currency_amount} />
        )}
        <BurnDownChart
          totalBudgetHours={totalBudgetHours}
          actualHoursUsed={actualHours}
          serverData={burndownData?.data}
        />
      </div>
      <div className="lg:col-span-2">
        <div className="card">
          <h2 className="text-base font-bold text-surface-800 mb-4">{t('project.tickets')}</h2>
          <TicketList tickets={tickets} />
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Development Layout
// ============================================================
function DevelopmentLayout({ milestones, isAdmin, onCreateMilestone }: {
  milestones: any[];
  isAdmin: boolean;
  onCreateMilestone: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-surface-800">{t('project.milestones')}</h2>
          {isAdmin && (
            <button onClick={onCreateMilestone}
              className="btn btn-primary flex items-center gap-2 text-sm">
              <Plus size={16} /> {t('project.newMilestone')}
            </button>
          )}
        </div>

        {milestones.length === 0 ? (
          <div className="card text-center py-8 text-surface-400 text-sm">{t('project.noMilestones')}</div>
        ) : (
          <div className="space-y-3">
            {milestones.map((ms) => (
              <MilestoneCard
                key={ms.id}
                id={ms.id}
                name={ms.name}
                description={ms.description}
                dueDate={ms.due_date}
                status={ms.status}
                ticketsTotal={ms.tickets_total || 0}
                ticketsClosed={ms.tickets_closed || 0}
                estimatedHours={ms.estimated_hours ? parseFloat(ms.estimated_hours) : undefined}
                actualHours={parseFloat(ms.total_actual_hours) || 0}
              />
            ))}
          </div>
        )}
      </div>

      <div className="space-y-4">
        <MilestoneProgressWidget
          milestones={milestones.map((ms) => ({
            id: ms.id,
            name: ms.name,
            status: ms.status,
            dueDate: ms.due_date,
            ticketsTotal: ms.tickets_total || 0,
            ticketsClosed: ms.tickets_closed || 0,
            estimatedHours: parseFloat(ms.estimated_hours) || 0,
            actualHours: parseFloat(ms.total_actual_hours) || 0,
          }))}
        />
      </div>
    </div>
  );
}

// ============================================================
// Project SLA Override Section
// ============================================================
function ProjectSlaOverride({ projectId, project }: { projectId: string; project: any }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [responseHours, setResponseHours] = useState(project.sla_response_hours ?? '');
  const [resolutionHours, setResolutionHours] = useState(project.sla_resolution_hours ?? '');

  useEffect(() => {
    setResponseHours(project.sla_response_hours ?? '');
    setResolutionHours(project.sla_resolution_hours ?? '');
  }, [project.sla_response_hours, project.sla_resolution_hours]);

  const mutation = useMutation({
    mutationFn: () =>
      api.updateProject(projectId, {
        sla_response_hours: responseHours === '' ? null : parseFloat(String(responseHours)),
        sla_resolution_hours: resolutionHours === '' ? null : parseFloat(String(resolutionHours)),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      setEditing(false);
    },
  });

  const hasOverride = project.sla_response_hours != null || project.sla_resolution_hours != null;

  if (!editing && !hasOverride) {
    return (
      <div className="flex items-center gap-2 text-sm text-surface-400">
        <Shield size={14} />
        <span>{t('sla.inheritFromOrg')}</span>
        <button onClick={() => setEditing(true)} className="text-primary-600 hover:underline text-xs">
          {t('sla.override')}
        </button>
      </div>
    );
  }

  if (!editing) {
    return (
      <div className="card p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 text-sm">
            <Shield size={16} className="text-primary-600" />
            {project.sla_response_hours != null && (
              <span className="text-surface-600">
                {t('sla.responseSla')}: <strong>{project.sla_response_hours}h</strong>
              </span>
            )}
            {project.sla_resolution_hours != null && (
              <span className="text-surface-600">
                {t('sla.resolutionSla')}: <strong>{project.sla_resolution_hours}h</strong>
              </span>
            )}
          </div>
          <button onClick={() => setEditing(true)} className="text-xs text-primary-600 hover:underline">
            {t('common.edit')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-4 space-y-3">
      <h3 className="text-sm font-semibold text-surface-700 flex items-center gap-2">
        <Shield size={16} className="text-primary-600" />
        {t('sla.override')}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="label text-xs">{t('sla.responseTime')}</label>
          <input type="number" step="0.5" min="0" className="input text-sm"
            placeholder={t('sla.inheritFromOrg')}
            value={responseHours}
            onChange={(e) => setResponseHours(e.target.value)} />
        </div>
        <div>
          <label className="label text-xs">{t('sla.resolutionTime')}</label>
          <input type="number" step="1" min="0" className="input text-sm"
            placeholder={t('sla.inheritFromOrg')}
            value={resolutionHours}
            onChange={(e) => setResolutionHours(e.target.value)} />
        </div>
      </div>
      {mutation.isError && (
        <div className="bg-danger-50 border border-danger-100 text-danger-700 px-3 py-2 rounded-lg text-xs">
          {t('common.saveError')}
        </div>
      )}
      <div className="flex gap-2">
        <button onClick={() => mutation.mutate()} disabled={mutation.isPending}
          className="btn btn-primary text-sm py-1.5">
          {mutation.isPending ? t('common.saving') : t('common.save')}
        </button>
        <button onClick={() => setEditing(false)} className="btn bg-surface-100 text-surface-700 text-sm py-1.5">
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
